import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService from '../services/authService';
import { setAccessToken, setActiveDelegationGrantId } from '../services/apiClient';
import { useDelegationStore } from './delegationStore';
import { clearWizardDraft } from './applicationStore';

/**
 * Tear down all delegation state held in this browser tab.
 *
 * Called on BOTH login and logout. sessionStorage survives a logout (it is only
 * cleared when the tab closes), so without this an account switch in the same
 * tab would leave the previous helper's grant id behind — poisoning the next
 * user's own requests with an X-Delegation-Grant-Id they don't own (→ 403
 * DELEGATION_USER_MISMATCH on their own application data). Clearing here on
 * login guarantees every session starts with no delegation context.
 */
function clearDelegationSession(): void {
    try {
        sessionStorage.removeItem('delegation_grantId');
        sessionStorage.removeItem('delegation_helperId');
        sessionStorage.removeItem('delegation_appId');
    } catch { /* sessionStorage unavailable */ }
    setActiveDelegationGrantId(null);
    // Clear any wizard draft that may have leaked from a delegation session
    // (helper editing someone else's app → draft saved with helper's userId)
    clearWizardDraft();
    try {
        useDelegationStore.getState().exitDelegation();
    } catch { /* delegation store not initialised yet */ }
}

export interface User {
    id: string;
    email: string;
    name: string;
    role: 'ADMIN' | 'MANAGER' | 'REVIEWER' | 'CUSTOMER' | 'BUILDER';
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (user: User, token: string) => void;
    logout: () => void;
    updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            token: null,
            isAuthenticated: false,

            login: (user, token) => {
                // Tear down any delegation context left over from a previous
                // session in this tab BEFORE establishing the new identity.
                clearDelegationSession();

                // Update both the in-memory token store AND localStorage.
                // MUST use setAccessToken() — never write localStorage directly,
                // or the apiClient request interceptor won't see the token.
                setAccessToken(token);

                set({
                    user,
                    token,
                    isAuthenticated: true,
                });
            },

            logout: async () => {
                try {
                    // Call logout API to invalidate token on server
                    await authService.logout();
                } catch (error) {
                    console.error('Logout error:', error);
                } finally {
                    // Clear local state regardless of API response
                    clearDelegationSession();
                    set({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                    });
                }
            },

            updateUser: (userData) =>
                set((state) => ({
                    user: state.user ? { ...state.user, ...userData } : null,
                })),
        }),
        {
            name: 'auth-storage',
        }
    )
);